import type { SimpleUser, UserGroupType, UserExtended } from '@/contexts/shared/users/Users.interfaces'
import type { ActionStatus } from '@/contexts/action-plans/ActionPlans.interfaces'
import type { EvalScaleType, PublicType } from '@/interfaces/Scale.interfaces'

export interface AssignedManager {
  id: number
  label: string
  type: UserGroupType
  email: string | null
}

export interface AssignedManagers {
  count: number
  list: AssignedManager[]
}

export enum EvaluationStatus {
  APPROVED = 'APPROVED',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  CHALLENGED = 'CHALLENGED',
}

export enum CampaignPerimeterStatus {
  INIT = 'INIT',
  IN_PROGRESS = 'IN_PROGRESS',
  EVAL_SUBMITED = 'EVAL_SUBMITED',
  EVAL_VALIDATED = 'EVAL_VALIDATED',
  FINISHED = 'FINISHED',
}
export enum CampaignTabStatus {
  ONGOING = 'ONGOING',
  SUBMITTED = 'SUBMITTED',
  CLOSED = 'CLOSED',
}

export enum EvaluationPerimeterType {
  INTERNAL = 'INTERNAL',
  PROVIDER = 'PROVIDER',
  Value3RDPARTY = '3RDPARTY',
  APPLICATION = 'APPLICATION',
}

export enum EvaluationAssociatedItemsState {
  NON_APPLICABLE = 'NON_APPLICABLE',
  APPLICABLE = 'APPLICABLE',
  REQUIRED = 'REQUIRED',
}

export enum EvaluationAssociatedObjectType {
  REMEDIATIONS = 'REMEDIATIONS',
  MEASURES = 'MEASURES',
}

export enum EvaluationStatusTab {
  IN_PROGRESS = 'IN_PROGRESS',
  SUBMITTED = 'SUBMITTED',
  VALIDATED = 'VALIDATED',
}

export interface EvaluationAnswer {
  id: number
  evaluator?: SimpleUser
  counter_evaluator?: SimpleUser
  control_id?: number
  perimeter_id?: number
  campaign_id?: number
  campaign_name?: string
  eval_level_id?: number
  counter_eval_level_id?: number
  percentage?: number
  counter_percentage?: number
  remediations?: EvaluationRemediation[]
  perimeter?: EvaluationPerimeter
  measures?: EvaluationMeasure[]
  has_counter_eval?: boolean
  files?: EvaluationFile[]
  url_proof?: string
  comments?: string
  counter_comments?: string
  score?: number
  date_created?: string
  color?: string
  level_name?: string
  eval_na?: string
  counter_eval_na?: string
  counter_eval_level?: EvaluationLevel
  status?: EvaluationStatus
  challenge_comments?: string
  challenge_user?: SimpleUser
  challenge_date?: string
}

export interface EvaluationRemediation {
  id: number
  identifier?: string
  name: string
  edit_right?: boolean
  view_right?: boolean
  delete_right?: boolean
  status?: ActionStatus
  completion?: number
  perimeter_id?: number
  perimeter?: EvaluationPerimeter
  ref?: string
  forked_solution_id?: number
  controls?: object[]
  measure_name?: string
  solution_name?: string
  action_type?: string
  controls_?: object[]
}

export interface EvaluationPerimeter {
  id?: number
  name?: string
  identifier?: string
  /** @example "INTERNAL" */
  type?: EvaluationPerimeterType
}

export interface EvaluationMeasure {
  id: number
  name: string
  edit_right?: boolean
  view_right?: boolean
  delete_right?: boolean
  perimeter_id?: number
  ref?: string
  forked_solution_id?: number
  controls?: object[]
  perimeter?: EvaluationPerimeter
  is_private?: boolean
  controls_?: object[]
  consume_perimeters?: EvaluationPerimeter[]
}

export interface EvaluationFile {
  id?: number
  filename?: string
  type?: string
  size?: number
  delete_right?: boolean
  view_right?: boolean
  date_created?: string
}

export interface EvaluationLevel {
  id: number
  eval_scale_id?: number
  name_fr?: string
  name_en?: string
  name: string
  description?: string
  description_fr?: string | null
  description_en?: string | null
  color: string
  low_value?: number
  high_value?: number
  remediation_state?: EvaluationAssociatedItemsState
  file_state?: EvaluationAssociatedItemsState
  measure_state?: EvaluationAssociatedItemsState
  comment_required?: boolean | null
}

export enum CampaignType {
  REGULAR = 'REGULAR',
  MAPPED = 'MAPPED',
}

export interface EvalScale {
  id: number
  name: string
  name_fr: string
  name_en: string
  identifier: string
  description: string
  description_fr: string | null
  description_en: string | null
  eval_levels: EvaluationLevel[]
  edit_right: boolean
  view_right: boolean
  delete_right: boolean
  edit_right_regular: boolean
  type: EvalScaleType
  public: boolean
  hide_scale: boolean
  hide_right: boolean
  public_type: PublicType
  availability: number | null
  integrity: number | null
  confidentiality: number | null
  proof: number | null
  custom_criteria: string | null
  has_prevent_delete: boolean | null
}

export interface CampaignProgram {
  id: number
  identifier: string
  name: string
}

export interface CampaignPolicyVersion {
  id: number
  policy_id: number
  name: string
  version_number: number
  identifier: string
}

export interface CampaignPolicy {
  id: number
  name: string
  public: boolean
  identifier: string
}

export interface CampaignQuestionnaire {
  id: number
  name: string
  identifier: string
}

export interface CampaignContribInfoResponse {
  objects?: {
    campaigns?: Campaign[]
  }
}

export interface Campaign {
  id: number
  name: string
  eval_scale: EvalScale
  non_applicable: boolean
  description: string
  status: string
  date_created: string
  tab_status: string
  type: CampaignType
  perimeter: EvaluationPerimeter
  tag: string | null
  mapping_done: number | null
  mapping_progress: number | null
  mapping_score: number | null
  limit_date_evaluation: string | null
  start_date: string
  program_id: number
  program: CampaignProgram
  isp_project_id: number
  completion: number
  policy_version: CampaignPolicyVersion
  policy: CampaignPolicy
  questionnaire: CampaignQuestionnaire | null
  edit_right: boolean
  export_right: boolean
}

export interface EvaluationPreviousAnswer {
  date_created: string
  control_id: number
  campaign_name: string
  campaign_id: number
  percentage?: number
  eval_level_color: string
  eval_level_name: string
  remediations?: EvaluationRemediation[]
  measures?: EvaluationMeasure[]
  score: number
  comments?: string
  files?: EvaluationFile[]
  url_proof?: string
  perimeter: EvaluationPerimeter
  eval_na: boolean
}

export interface EvaluationComment {
  id: number
  comment: string
  timestamp: string
  user?: UserExtended | null
  from_me: boolean
}
